import React, { useContext, useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  CompanyModuleEnum,
  GetLinkedCodeOutDTO,
  GetLinkedCompanyOutDTO,
  ModulesEnum,
  PermissionTypesEnum,
} from '@api/ringil-api.v2';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Checkbox } from 'primereact/checkbox';
import { ApiContext } from '@api/api';
import { useTranslation } from 'react-i18next';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import clsx from 'clsx';
import { FaTrash } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RepeatSvg from '@icons/Repeat.tsx';
import SvgCoCopy from '@icons/CoCopy.tsx';
import ConfirmCodeRefreshDialog from '@app/pages/settings/ConfirmCodeRefreshDialog.tsx';

interface ComponentProps {
}

export interface AllPartnersFilter {
  companyName?: string;
  companyModules?: CompanyModuleEnum[];
}

interface ActionPanelComponentProps {
  selectedCount: number;
  onSelectAllChange: (checked: boolean) => void;
  selectAllChecked: boolean;
  onApprove: () => void;
  onDelete: () => void;
  onSaveCSV: () => void;
  isMobile: boolean;
}

const PartnerCodes: React.FC<ComponentProps> = () => {
  const { t, i18n } = useTranslation('common');
  const { tr } = useTranslationLgs();
  const [linkedCodeData, setLinkedCodeData] = useState<GetLinkedCodeOutDTO[]>();
  const [loadingLinkedCodeData, setLoadingLinkedCodeData] = useState(false);
  const {
    getActiveLinkedCodes,
    regenerateCode,
  } = useContext(ApiContext);
  const { loggedUser, hasPermission } = useContext(AuthenticatedUserContext);
  const hasActionPermission = hasPermission([PermissionTypesEnum.PERMISSION_SETTINGS_PARTNERS_RW]);
  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const [selectedObjects, setSelectedObjects] = useState<GetLinkedCompanyOutDTO[]>([]);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [selectedCodeForRefresh, setSelectedCodeForRefresh] = useState<GetLinkedCodeOutDTO | null>(null);

  const toggleCheckbox = (object: GetLinkedCompanyOutDTO) => {
    const found = selectedObjects.some(t => t.companyId === object.companyId);
    if (found) setSelectedObjects(selectedObjects.filter(t => t.companyId !== object.companyId));
    else setSelectedObjects([...selectedObjects, object]);
  };

  const isSelected = (object: GetLinkedCompanyOutDTO) => {
    return selectedObjects.some(t => t.companyId === object.companyId);
  };

  const loadActiveCodes = () => {
    setLoadingLinkedCodeData(true);
    getActiveLinkedCodes(data => {
      setLinkedCodeData(data?.sort((a, b) => {
        return a.order - b.order;
      }));
      setLoadingLinkedCodeData(false);
    });
  };

  useEffect(() => {
    loadActiveCodes();
  }, []);



  const protocol = window.location.protocol;
  const host = window.location.host;

  const handleMailto = (row: GetLinkedCodeOutDTO) => {
    if (!row) return;

    const moduleNames = encodeURIComponent(row.modules?.map(module =>
      tr('PartnerCodes.mailtoModule' + module, module),
    ).reduce((acc, curr, idx, arr) => {
      if (idx === 0) return curr;
      if (idx === arr.length - 1) return `${acc} ${tr('PartnerCodes.mailtoModuleJoin', 'and')} ${curr}`;
      return `${acc}, ${curr}`;
    }, '') || '');

    const subject = tr('PartnerCodes.mailtoSubject', 'You%20are%20invited%20to%20the%20Ringil%20logistics%20platform');

    const body = tr('PartnerCodes.mailtoBody', `Hello,%0A%0AOn%20behalf%20of%20{{companyName}},%20I%20would%20like%20to%20invite%20you%20to%20use%20the%20Ringil%20logistics%20platform.%20We%20are%20interested%20in%20you%20starting%20to%20use%20{{moduleNames}}.%20Using%20Ringil%20will%20be%20free%20of%20charge%20for%20you.%0A%0ATo%20create%20your%20company%20registration,%20please%20use%20the%20following%20link:%0A%0A{{protocol}}//{{host}}/{{language}}/public/registration?registrationCode={{code}}%20%0A%0AIf%20the%20link%20does%20not%20work%20or%20the%20activation%20code%20is%20not%20transferred,%20enter%20app.ringil.com/register%20and%20enter%20the%20registration%20code%20{{code}}%20in%20the%20registration%20code%20field.%0A%0AHave%20a%20nice%20day.`, {
      companyName: loggedUser.company,
      moduleNames: moduleNames,
      code: row.code,
      protocol: protocol,
      host: host,
      language: i18n.language,
    });

    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    console.log(mailtoUrl);

    window.open(mailtoUrl);
  };

  const handleRegisterPartner = (row: GetLinkedCodeOutDTO) => {
    if (!row) return;
    const url = `${protocol}//${host}/${i18n.language}/public/registration?registrationCode=${row.code}`;
    window.open(url);
  };

  const handleRefreshCode = (row: GetLinkedCodeOutDTO) => {
    if (!hasActionPermission || row.modules?.length === 0) return;

    setSelectedCodeForRefresh(row);
    setConfirmDialogVisible(true);
  };

  const handleConfirmRefresh = () => {
    if (!selectedCodeForRefresh) return;

    regenerateCode({
      modules: selectedCodeForRefresh.modules,
    }, () => {
      loadActiveCodes();
      setConfirmDialogVisible(false);
      setSelectedCodeForRefresh(null);
    });
  };

  const handleCancelRefresh = () => {
    setConfirmDialogVisible(false);
    setSelectedCodeForRefresh(null);
  };

  const renderCheckboxColumn = (row: GetLinkedCompanyOutDTO) => {
    return (
      <div className="flex gap-2 items-center min-h-10">
        <Checkbox
          onChange={e => {
            toggleCheckbox(row);
          }}
          checked={isSelected(row)}
        />
      </div>
    );
  };

  const renderModulesColumn = (row: GetLinkedCodeOutDTO) => {
    return (
      <div className="flex gap-2 items-center min-h-10">
        {row.modules?.join(', ')}
      </div>

    );
  };

  const renderCodesColumn = (row: GetLinkedCodeOutDTO, disabled: boolean) => {
    return (
      <div className="flex gap-2 items-center min-h-10">
        <RingilButton
          mode={'table'}
          disabled={disabled}
          onClick={() => navigator.clipboard.writeText(row.code)}
          icon={!disabled && <SvgCoCopy style={{ color: 'var(--primary-color)' }} />}
          label={row.code}
        />
      </div>
    );
  };

  const renderCreatedATColumn = (row: GetLinkedCodeOutDTO) => {
    return (
      <div className="flex gap-2 items-center min-h-10">
        {[
          row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '',
          row.createByFullname,
        ].filter(Boolean).join(', ')}
      </div>
    );
  };

  const renderCodeActions = (row: GetLinkedCodeOutDTO) => {

    if (!hasActionPermission) {
      return <div></div>;
    }

    return (
      <div className="flex gap-2 items-center justify-end">
        <RingilButton
          mode={'table'}
          label={tr('PartnerCodes.registerPartner', 'Register partner')}
          onClick={() => handleRegisterPartner(row)}
        />
        <RingilButton
          mode={'table'}
          label={tr('PartnerCodes.mailto', 'Mailto')}
          onClick={() => handleMailto(row)}
        />
        <RingilButton
          mode={'table'}
          onClick={() => handleRefreshCode(row)}
          icon={<RepeatSvg />}
        />
      </div>
    );
  };

  const renderCodesMobileCard = (row: GetLinkedCodeOutDTO) => {

    return (
      <div key={row.code}
           className="bg-white p-8 pb-6 shadow-[0_1px_0_0_rgba(229,231,235,1)] -mx-4 relative z-10 mb-1">
        {renderModulesColumn(row)}
        {renderCodesColumn(row, false)}
        {renderCreatedATColumn(row)}
        {renderCodeActions(row)}
      </div>
    )
      ;
  };

  const renderArchiveCodesMobileCard = (row: GetLinkedCodeOutDTO) => {

    return (
      <div key={row.code}
           className="bg-white p-8 pb-6 shadow-[0_1px_0_0_rgba(229,231,235,1)] -mx-4 relative z-10 mb-1">
        {renderModulesColumn(row)}
        {renderCreatedATColumn(row)}
        {renderCreatedATColumn(row)}
        {renderCodesColumn(row, true)}
      </div>
    )
      ;
  };

  return (
    <>
      <div className={clsx('r-legacy-list-fields', 'font-[\'DM Sans\']', isMobile && '-mx-4 px-8 pt-8')}>
        <div className="flex justify-between items-center mb-6">
          <Heading className="!m-0">{t(`PartnerCodes.titleCodes`, `Codes`)}</Heading>
        </div>
      </div>

      <div className="datatable-filter p-datatable-customers">
        {isMobile ? (
          <div>
            {linkedCodeData && linkedCodeData.length > 0 ? (
              linkedCodeData.map(row => renderCodesMobileCard(row))
            ) : (
              <div className="p-4 text-center">{t('global.noRecordFound')}</div>
            )}
          </div>
        ) : (
          <DataTable
            emptyMessage={t('global.noRecordFound')}
            value={linkedCodeData}
            className="p-datatable-customers"
            lazy
            loading={loadingLinkedCodeData}
          >
            <Column
              header={tr(`PartnerCodes.dataTableHeaderTitleModules`, `Modules`)}
              body={row => renderModulesColumn(row)}
              style={{ width: '25%' }}
            />
            <Column
              header={tr(`PartnerCodes.dataTableHeaderTitleCodes`, `Code`)}
              body={row => renderCodesColumn(row, false)}
              style={{ width: '35%' }}
            />
            <Column
              header={tr('PartnerCodes.dataTableHeaderTitleCreatedAt', 'Created at')}
              body={row => renderCreatedATColumn(row)}
              style={{ width: '15%' }}
            />
            <Column body={row => renderCodeActions(row)} />
          </DataTable>
        )}
      </div>

      <ConfirmCodeRefreshDialog
        modules={selectedCodeForRefresh?.modules}
        visible={confirmDialogVisible}
        onHide={handleCancelRefresh}
        onConfirm={handleConfirmRefresh}
      />

    </>
  );
};

export default PartnerCodes;


const ActionButton = styled.button<{ disabled?: boolean }>`
  margin: 0 8px;
  color: #ff6b00;
  background: none;
  border: none;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  font-family: 'DM Sans', sans-serif;
  font-size: 1rem;
  padding: 0;
  transition: opacity 0.2s ease-in-out;
  white-space: nowrap;

  &:hover {
    opacity: ${props => (props.disabled ? 0.5 : 0.8)};
  }
`;

const DeleteButton = styled(FaTrash)`
  color: #fe5000;
  margin-left: 16px;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 16px;
  background: none;
  border: none;
  font-weight: 400;
  cursor: pointer;
  font-family: inherit;
  font-size: 1rem;
  color: #fe5000;
  padding: 0;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`;

const ArrowIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin: 0 8px 0 8px;
  color: #b0b0b0;
  font-size: 18px;

  svg {
    display: block;
  }
`;

const Heading = styled.h1`
  padding: 0;
  font-size: 26px;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
`;
